import { t } from '@lingui/core/macro';
import { UserType } from '@luminovo/api-contracts';
import { GlobalCommandPalette as GlobalCommandPaletteComponent, GlobalCommandPaletteInput } from '@luminovo/data-stack';
import * as Sentry from '@sentry/react';
import React, { useMemo, useState } from 'react';

import { useCurrentUserDetailsContext } from '@/shared/data/contexts/CurrentUserDetailsContext';

import { useFullPageSlotCommands } from './useFullPageSlotCommands';
import { useNavigationCommands } from './useNavigationCommands';
import { useSearchableResources } from './useSearchableResources';
import { useTestingToolCommands } from './useTestingToolCommands';

type GlobalCommandPaletteProps = {
    telemetryLocation: string;
    variant?: 'dark' | 'light';
    /** When false, only the keyboard shortcut opens the palette, for navbars short on space. */
    showInput?: boolean;
    /** Controlled open state, for a custom trigger such as `CommandPaletteSearchButton`. */
    open?: boolean;
    /** Required when `open` is provided. */
    onOpenChange?: (open: boolean) => void;
    /** The new navigation's: its item opens the Lumi Agent page, and Ctrl/Cmd+I is the shell's, not the drawer's. */
    onLumiAgent?: () => void;
};

/** The palette's suspending data and hooks; render inside a Suspense and an ErrorBoundary. */
function DataLoader({
    open,
    onOpenChange,
    telemetryLocation,
    onLumiAgent,
}: {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    telemetryLocation: string;
    onLumiAgent?: () => void;
}): JSX.Element | null {
    const { userType } = useCurrentUserDetailsContext();
    const navigationCommands = useNavigationCommands(onLumiAgent);
    const fullPageSlotCommands = useFullPageSlotCommands();
    const testingToolCommands = useTestingToolCommands();
    const commands = useMemo(
        () => [...navigationCommands, ...fullPageSlotCommands, ...testingToolCommands],
        [fullPageSlotCommands, navigationCommands, testingToolCommands],
    );
    const searchableResources = useSearchableResources();

    if (userType !== UserType.Internal) {
        return null;
    }

    return (
        <GlobalCommandPaletteComponent
            open={open}
            onOpenChange={onOpenChange}
            commands={commands}
            searchableResources={searchableResources}
            placeholder={t`Search...`}
            telemetry={{ location: telemetryLocation }}
        />
    );
}

/** Isolated in its own Suspense and ErrorBoundary; controlled when given `open`. */
export function GlobalCommandPalette({
    telemetryLocation,
    variant = 'dark',
    showInput = true,
    open: controlledOpen,
    onOpenChange: controlledOnOpenChange,
    onLumiAgent,
}: GlobalCommandPaletteProps): JSX.Element | null {
    const { userType } = useCurrentUserDetailsContext();
    const [internalOpen, setInternalOpen] = useState(false);
    const isControlled = controlledOpen !== undefined;
    const open = isControlled ? controlledOpen : internalOpen;
    const setOpen = isControlled ? (controlledOnOpenChange ?? (() => {})) : setInternalOpen;

    if (userType !== UserType.Internal) {
        return null;
    }

    return (
        <>
            <Sentry.ErrorBoundary fallback={<></>}>
                <React.Suspense fallback={null}>
                    <DataLoader
                        open={open}
                        onOpenChange={setOpen}
                        telemetryLocation={telemetryLocation}
                        onLumiAgent={onLumiAgent}
                    />
                </React.Suspense>
            </Sentry.ErrorBoundary>
            {showInput && <GlobalCommandPaletteInput open={open} onOpen={() => setOpen(true)} variant={variant} />}
        </>
    );
}
